// src/components/StaffManagement.jsx
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import config from "../config";
import "../css/theme.css";
import "../css/components.css";
import "./StaffManagement.css";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import { toLocalDateStr, formatDateDMY } from "../utils/date";
import ToastContainer, { useToast } from "./common/Toast";

// Matches AdminSalaryManagement.jsx's own MONTH_NAMES - this codebase
// duplicates this small array per-file rather than sharing one.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_OPTIONS = MONTH_NAMES.map((m, idx) => ({ value: idx + 1, label: m }));
const todayDate = new Date();
const YEAR_OPTIONS = [todayDate.getFullYear() - 1, todayDate.getFullYear(), todayDate.getFullYear() + 1]
  .map((y) => ({ value: y, label: String(y) }));

const ALL_OPTION = { value: null, label: "All" };

async function fetchJson(url) {
  const res = await fetch(url, { headers: config.getHeaders() });
  if (!res.ok) throw new Error("Request failed");
  return res.json();
}

// Phone fields only ever accept up to 10 digits - strips anything else as
// it's typed/pasted rather than rejecting on submit, so the mismatch is
// never a surprise.
const toPhoneDigits = (value) => value.replace(/\D/g, "").slice(0, 10);
const isValidPhone = (value) => /^\d{10}$/.test(value);

// Reattaches the backend's "?access=<signed-token>" query string onto the
// /api/files/** URL, same pattern as GuestCheckInPage.jsx's own
// getFileViewUrl - the backend already returns relativePath+token baked
// together (see StaffService.buildFileAccessUrl), this just rebuilds the
// full URL and encodes each path segment individually so "/" stays literal.
const getFileViewUrl = (filePathOrName) => {
  if (!filePathOrName) return "";
  const [pathPart, queryPart] = filePathOrName.split("?");
  const encodedPath = pathPart.split("/").map((s) => encodeURIComponent(s)).join("/");
  const base = `${config.BASE_URL}/api/files/${encodedPath}`;
  return queryPart ? `${base}?${queryPart}` : base;
};

/* ----------------------------- Staff tab ----------------------------- */

function StaffTab({ resortOptions, showToast }) {
  const [resortFilter, setResortFilter] = useState(ALL_OPTION);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null); // null = closed, {} = new, {...} = editing
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Which ID document field is currently mid-upload ("idDocument1" |
  // "idDocument2" | null) - uploads happen immediately on file select
  // (same pattern as ResortDetailsService's uploadResortPhoto), not
  // deferred to the main Save.
  const [uploadingDocField, setUploadingDocField] = useState(null);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const url = resortFilter.value
        ? `${config.BASE_URL}/api/staff?resortId=${resortFilter.value}`
        : `${config.BASE_URL}/api/staff`;
      setStaff(await fetchJson(url));
    } catch (err) {
      console.error(err);
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resortFilter]);

  const openNewStaffModal = () => {
    setEditingStaff({
      name: "",
      phoneNumber: "",
      emergencyContactName: "",
      emergencyContactPhone: "",
      designation: "",
      dateOfJoining: null,
      monthlySalary: "",
      address: "",
      resort: resortFilter.value ? resortFilter : null,
      active: true,
      // No staff id yet to upload against - these File objects are staged
      // locally and uploaded right after the staff record is created (see
      // handleSubmitStaff), instead of being hidden until a second Edit.
      pendingIdDocument1: null,
      pendingIdDocument2: null,
    });
  };

  const openEditStaffModal = (row) => {
    setEditingStaff({
      id: row.id,
      name: row.name,
      phoneNumber: row.phoneNumber,
      emergencyContactName: row.emergencyContactName || "",
      emergencyContactPhone: row.emergencyContactPhone || "",
      designation: row.designation || "",
      dateOfJoining: row.dateOfJoining ? new Date(row.dateOfJoining + "T00:00:00") : null,
      monthlySalary: row.monthlySalary ?? "",
      address: row.address || "",
      resort: resortOptions.find((r) => r.value === row.resortId) || null,
      active: row.active,
      idDocument1Url: row.idDocument1Url || null,
      idDocument2Url: row.idDocument2Url || null,
    });
  };

  // Low-level upload, reused by both the immediate-upload path (editing an
  // existing staff member) and the just-after-create path (brand new one -
  // see handleSubmitStaff). Deliberately not config.getHeaders() - that
  // forces Content-Type: application/json, which breaks the multipart
  // boundary the browser needs to set itself for FormData (same reasoning
  // as ResortDetailsService.uploadPhoto).
  const uploadStaffIdDocumentFile = async (staffId, field, file) => {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append(field, file);
    const res = await fetch(`${config.BASE_URL}/api/staff/${staffId}/id-documents`, {
      method: "POST",
      headers: { Authorization: token ? `Bearer ${token}` : "" },
      body: formData,
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => null);
      throw new Error(errBody?.error || "Failed to upload document");
    }
    return res.json();
  };

  // Immediate upload for an EXISTING staff member - a brand-new one with no
  // id yet just stages the file locally instead (see the file input's
  // onChange below).
  const handleUploadIdDocument = async (field, file) => {
    if (!editingStaff?.id || !file) return;
    setUploadingDocField(field);
    try {
      const updated = await uploadStaffIdDocumentFile(editingStaff.id, field, file);
      setEditingStaff((prev) =>
        prev
          ? { ...prev, idDocument1Url: updated.idDocument1Url, idDocument2Url: updated.idDocument2Url }
          : prev
      );
      showToast("Document uploaded.", "success");
      fetchStaff();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to upload document. Please try again.", "danger");
    } finally {
      setUploadingDocField(null);
    }
  };

  // While adding a brand-new staff member (no id yet), picking a file just
  // stages it here - handleSubmitStaff uploads it right after the create
  // succeeds. While editing an existing one, it uploads immediately.
  const handleSelectIdDocumentFile = (field, file) => {
    if (!file) return;
    if (editingStaff?.id) {
      handleUploadIdDocument(field, file);
    } else {
      setEditingStaff((prev) => (prev ? { ...prev, [field === "idDocument1" ? "pendingIdDocument1" : "pendingIdDocument2"]: file } : prev));
    }
  };

  const handleSubmitStaff = async () => {
    if (!editingStaff || isSubmitting) return;
    if (!editingStaff.name.trim() || !editingStaff.phoneNumber.trim()) {
      showToast("Name and phone number are required.", "warning");
      return;
    }
    if (!isValidPhone(editingStaff.phoneNumber)) {
      showToast("Phone number must be exactly 10 digits.", "warning");
      return;
    }
    if (editingStaff.emergencyContactPhone && !isValidPhone(editingStaff.emergencyContactPhone)) {
      showToast("Emergency contact phone must be exactly 10 digits.", "warning");
      return;
    }
    if (!editingStaff.resort) {
      showToast("Please select a resort.", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      const body = {
        name: editingStaff.name.trim(),
        phoneNumber: editingStaff.phoneNumber.trim(),
        emergencyContactName: editingStaff.emergencyContactName.trim() || null,
        emergencyContactPhone: editingStaff.emergencyContactPhone.trim() || null,
        designation: editingStaff.designation.trim() || null,
        dateOfJoining: editingStaff.dateOfJoining ? toLocalDateStr(editingStaff.dateOfJoining) : null,
        monthlySalary: editingStaff.monthlySalary === "" ? null : parseFloat(editingStaff.monthlySalary),
        address: editingStaff.address.trim() || null,
        resortId: editingStaff.resort.value,
        active: editingStaff.active,
      };

      const url = editingStaff.id
        ? `${config.BASE_URL}/api/staff/${editingStaff.id}`
        : `${config.BASE_URL}/api/staff`;
      const res = await fetch(url, {
        method: editingStaff.id ? "PUT" : "POST",
        headers: config.getHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || "Failed to save staff record");
      }

      // A brand-new staff member has no id to upload against until now -
      // any ID documents picked while filling out the form get uploaded
      // right after the record itself is created.
      if (!editingStaff.id && (editingStaff.pendingIdDocument1 || editingStaff.pendingIdDocument2)) {
        const created = await res.json();
        try {
          if (editingStaff.pendingIdDocument1) {
            await uploadStaffIdDocumentFile(created.id, "idDocument1", editingStaff.pendingIdDocument1);
          }
          if (editingStaff.pendingIdDocument2) {
            await uploadStaffIdDocumentFile(created.id, "idDocument2", editingStaff.pendingIdDocument2);
          }
        } catch (uploadErr) {
          console.error(uploadErr);
          showToast("Staff saved, but a document failed to upload. Edit the staff member to retry.", "warning");
        }
      }

      showToast(editingStaff.id ? "Staff updated." : "Staff added.", "success");
      setEditingStaff(null);
      fetchStaff();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to save staff record. Please try again.", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="staff-management-filters">
        <label>Resort</label>
        <Select
          options={[ALL_OPTION, ...resortOptions]}
          value={resortFilter}
          onChange={setResortFilter}
          isSearchable={false}
          classNamePrefix="react-select"
          className="react-select-container filter-select"
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          styles={themedSelectStyles()}
        />
        <button className="vt-btn vt-btn-primary" onClick={openNewStaffModal} style={{ marginLeft: "auto" }}>
          + Add Staff
        </button>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      {editingStaff && (
        <div className="modal staff-form-card">
            <h3>{editingStaff.id ? "Edit Staff" : "Add Staff"}</h3>

            <div className="modal-field">
              <label>Name:</label>
              <input
                type="text"
                value={editingStaff.name}
                onChange={(e) => setEditingStaff({ ...editingStaff, name: e.target.value })}
              />
            </div>

            <div className="modal-field">
              <label>Phone Number:</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={editingStaff.phoneNumber}
                onChange={(e) => setEditingStaff({ ...editingStaff, phoneNumber: toPhoneDigits(e.target.value) })}
              />
            </div>

            <div className="modal-field">
              <label>Emergency Contact Name:</label>
              <input
                type="text"
                value={editingStaff.emergencyContactName}
                onChange={(e) => setEditingStaff({ ...editingStaff, emergencyContactName: e.target.value })}
              />
            </div>

            <div className="modal-field">
              <label>Emergency Contact Phone:</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={editingStaff.emergencyContactPhone}
                onChange={(e) => setEditingStaff({ ...editingStaff, emergencyContactPhone: toPhoneDigits(e.target.value) })}
              />
            </div>

            <div className="modal-field">
              <label>Designation:</label>
              <input
                type="text"
                value={editingStaff.designation}
                onChange={(e) => setEditingStaff({ ...editingStaff, designation: e.target.value })}
                placeholder="e.g. Housekeeping, Cook, Security"
              />
            </div>

            <div className="modal-field">
              <label>Date of Joining:</label>
              <DatePicker
                selected={editingStaff.dateOfJoining}
                onChange={(date) => setEditingStaff({ ...editingStaff, dateOfJoining: date })}
                dateFormat="dd/MM/yyyy"
                className="custom-datepicker"
                isClearable
              />
            </div>

            <div className="modal-field">
              <label>Monthly Salary (₹):</label>
              <input
                type="number"
                value={editingStaff.monthlySalary}
                onChange={(e) => setEditingStaff({ ...editingStaff, monthlySalary: e.target.value })}
              />
            </div>

            <div className="modal-field">
              <label>Address:</label>
              <textarea
                value={editingStaff.address}
                onChange={(e) => setEditingStaff({ ...editingStaff, address: e.target.value })}
              />
            </div>

            <div className="modal-field">
              <label>Resort:</label>
              <Select
                options={resortOptions}
                value={editingStaff.resort}
                onChange={(opt) => setEditingStaff({ ...editingStaff, resort: opt })}
                placeholder="Select resort..."
                classNamePrefix="react-select"
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>

            {editingStaff.id && (
              <div className="modal-field">
                <label>
                  <input
                    type="checkbox"
                    checked={editingStaff.active}
                    onChange={(e) => setEditingStaff({ ...editingStaff, active: e.target.checked })}
                    style={{ marginRight: "8px", width: "auto" }}
                  />
                  Active
                </label>
              </div>
            )}

            <div className="modal-field staff-id-doc-field">
              <label>ID Document 1 (e.g. Aadhar):</label>
              {editingStaff.idDocument1Url && (
                <p className="staff-id-doc-link">
                  <a href={getFileViewUrl(editingStaff.idDocument1Url)} target="_blank" rel="noopener noreferrer">
                    View uploaded document
                  </a>
                </p>
              )}
              {!editingStaff.id && editingStaff.pendingIdDocument1 && (
                <p className="staff-id-doc-link">Selected: {editingStaff.pendingIdDocument1.name} (uploads after you save)</p>
              )}
              <input
                type="file"
                id="staff-id-document-1"
                accept="image/*,.pdf"
                onChange={(e) => handleSelectIdDocumentFile("idDocument1", e.target.files[0])}
                disabled={uploadingDocField === "idDocument1"}
                className="staff-id-doc-field__input"
              />
              <label htmlFor="staff-id-document-1" className="staff-id-doc-field__upload-btn">
                {uploadingDocField === "idDocument1"
                  ? "Uploading..."
                  : editingStaff.idDocument1Url || editingStaff.pendingIdDocument1
                  ? "Replace File"
                  : "Choose File"}
              </label>
            </div>

            <div className="modal-field staff-id-doc-field">
              <label>ID Document 2 (e.g. PAN):</label>
              {editingStaff.idDocument2Url && (
                <p className="staff-id-doc-link">
                  <a href={getFileViewUrl(editingStaff.idDocument2Url)} target="_blank" rel="noopener noreferrer">
                    View uploaded document
                  </a>
                </p>
              )}
              {!editingStaff.id && editingStaff.pendingIdDocument2 && (
                <p className="staff-id-doc-link">Selected: {editingStaff.pendingIdDocument2.name} (uploads after you save)</p>
              )}
              <input
                type="file"
                id="staff-id-document-2"
                accept="image/*,.pdf"
                onChange={(e) => handleSelectIdDocumentFile("idDocument2", e.target.files[0])}
                disabled={uploadingDocField === "idDocument2"}
                className="staff-id-doc-field__input"
              />
              <label htmlFor="staff-id-document-2" className="staff-id-doc-field__upload-btn">
                {uploadingDocField === "idDocument2"
                  ? "Uploading..."
                  : editingStaff.idDocument2Url || editingStaff.pendingIdDocument2
                  ? "Replace File"
                  : "Choose File"}
              </label>
            </div>

            <div className="modal-actions">
              <button className="checkin-btn" onClick={handleSubmitStaff} disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save"}
              </button>
              <button className="cancel-btn" onClick={() => setEditingStaff(null)}>
                Cancel
              </button>
            </div>
        </div>
      )}

      <div className="attendance-roster-card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Designation</th>
                <th>Resort</th>
                <th>Monthly Salary</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {staff.length > 0 ? (
                staff.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.phoneNumber}</td>
                    <td>{s.designation || "-"}</td>
                    <td>{s.resortName}</td>
                    <td>{s.monthlySalary != null ? `₹${Number(s.monthlySalary).toLocaleString()}` : "-"}</td>
                    <td>
                      <span className={`vt-badge ${s.active ? "vt-badge-success" : "vt-badge-danger"}`}>
                        {s.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button className="vt-btn vt-btn-purple" onClick={() => openEditStaffModal(s)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>No staff found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ---------------------------- Shared staff-filter hook ---------------------------- */

function useStaffOptions(resortId) {
  const [allStaff, setAllStaff] = useState([]);

  useEffect(() => {
    const url = resortId
      ? `${config.BASE_URL}/api/staff?resortId=${resortId}`
      : `${config.BASE_URL}/api/staff`;
    fetchJson(url)
      .then((data) => setAllStaff(data || []))
      .catch(() => setAllStaff([]));
  }, [resortId]);

  // Filter dropdown (page-level) can show everyone, including staff who've
  // since left, so historical records are still findable - the ACTIVE-only
  // list is for the mark/add modals, which shouldn't offer someone who's
  // no longer on staff.
  const allOptions = useMemo(
    () => allStaff.map((s) => ({ value: s.id, label: s.name, resortId: s.resortId, monthlySalary: s.monthlySalary })),
    [allStaff]
  );
  const activeOptions = useMemo(
    () => allStaff.filter((s) => s.active).map((s) => ({ value: s.id, label: s.name, resortId: s.resortId, monthlySalary: s.monthlySalary })),
    [allStaff]
  );

  return { allOptions, activeOptions };
}

/* ----------------------------- Leaves tab ----------------------------- */

function LeavesTab({ resortOptions, showToast }) {
  const [resortFilter, setResortFilter] = useState(ALL_OPTION);
  const [staffFilter, setStaffFilter] = useState(ALL_OPTION);
  const [month, setMonth] = useState(todayDate.getMonth() + 1);
  const [year, setYear] = useState(todayDate.getFullYear());
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);

  const { allOptions: staffOptions, activeOptions: activeStaffOptions } = useStaffOptions(resortFilter.value);

  // Resort filter changed - staff filter from the PREVIOUS resort no longer
  // applies, so it resets rather than silently filtering on a stale id.
  useEffect(() => {
    setStaffFilter(ALL_OPTION);
  }, [resortFilter]);

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (resortFilter.value) params.set("resortId", resortFilter.value);
      if (staffFilter.value) params.set("staffId", staffFilter.value);
      setLeaves(await fetchJson(`${config.BASE_URL}/api/staff-leaves?${params.toString()}`));
    } catch (err) {
      console.error(err);
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resortFilter, staffFilter, month, year]);

  const [showMarkModal, setShowMarkModal] = useState(false);
  const [markStaff, setMarkStaff] = useState(null);
  const [markFrom, setMarkFrom] = useState(null);
  const [markTo, setMarkTo] = useState(null);
  const [markReason, setMarkReason] = useState("");
  const [isSubmittingMark, setIsSubmittingMark] = useState(false);

  const openMarkModal = () => {
    setMarkStaff(null);
    setMarkFrom(null);
    setMarkTo(null);
    setMarkReason("");
    setShowMarkModal(true);
  };

  const handleMarkLeave = async () => {
    if (isSubmittingMark) return;
    if (!markStaff) {
      showToast("Select a staff member.", "warning");
      return;
    }
    if (!markFrom) {
      showToast("Select a date (or date range).", "warning");
      return;
    }
    const to = markTo || markFrom;
    if (to < markFrom) {
      showToast("End date can't be before the start date.", "warning");
      return;
    }

    setIsSubmittingMark(true);
    try {
      const res = await fetch(`${config.BASE_URL}/api/staff-leaves/mark`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          staffId: markStaff.value,
          fromDate: toLocalDateStr(markFrom),
          toDate: toLocalDateStr(to),
          reason: markReason.trim() || null,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || "Failed to mark leave");
      }

      showToast("Leave marked.", "success");
      setShowMarkModal(false);
      fetchLeaves();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to mark leave. Please try again.", "danger");
    } finally {
      setIsSubmittingMark(false);
    }
  };

  return (
    <>
      <div className="staff-management-filters">
        <label>Resort</label>
        <Select
          options={[ALL_OPTION, ...resortOptions]}
          value={resortFilter}
          onChange={setResortFilter}
          isSearchable={false}
          classNamePrefix="react-select"
          className="react-select-container filter-select"
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          styles={themedSelectStyles()}
        />
        <label>Staff</label>
        <Select
          options={[ALL_OPTION, ...staffOptions]}
          value={staffFilter}
          onChange={setStaffFilter}
          isSearchable
          classNamePrefix="react-select"
          className="react-select-container filter-select"
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          styles={themedSelectStyles()}
        />
        <div className="month-picker">
          <Select
            options={MONTH_OPTIONS}
            value={MONTH_OPTIONS.find((o) => o.value === month)}
            onChange={(opt) => setMonth(opt.value)}
            isSearchable={false}
            classNamePrefix="react-select"
            className="react-select-container filter-select"
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            styles={themedSelectStyles()}
          />
          <Select
            options={YEAR_OPTIONS}
            value={YEAR_OPTIONS.find((o) => o.value === year)}
            onChange={(opt) => setYear(opt.value)}
            isSearchable={false}
            classNamePrefix="react-select"
            className="react-select-container filter-select"
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            styles={themedSelectStyles()}
          />
        </div>
        <button className="vt-btn vt-btn-primary" onClick={openMarkModal} style={{ marginLeft: "auto" }}>
          + Mark Leave
        </button>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="attendance-roster-card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Staff</th>
                <th>Resort</th>
                <th>Reason</th>
                <th>Marked By</th>
              </tr>
            </thead>
            <tbody>
              {leaves.length > 0 ? (
                leaves.map((l) => (
                  <tr key={l.id}>
                    <td>{formatDateDMY(l.date)}</td>
                    <td>{l.staffName}</td>
                    <td>{l.resortName}</td>
                    <td>{l.reason || "-"}</td>
                    <td>{l.markedByName || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>No leave records for this month</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showMarkModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Mark Leave</h3>

            <div className="modal-field">
              <label>Staff:</label>
              <Select
                options={activeStaffOptions}
                value={markStaff}
                onChange={setMarkStaff}
                placeholder="Select staff..."
                classNamePrefix="react-select"
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>

            <div className="modal-field">
              <label>From:</label>
              <DatePicker
                selected={markFrom}
                onChange={(date) => setMarkFrom(date)}
                dateFormat="dd/MM/yyyy"
                className="custom-datepicker"
              />
            </div>

            <div className="modal-field">
              <label>To (optional - same day if blank):</label>
              <DatePicker
                selected={markTo}
                onChange={(date) => setMarkTo(date)}
                dateFormat="dd/MM/yyyy"
                className="custom-datepicker"
                minDate={markFrom}
                isClearable
              />
            </div>

            <div className="modal-field">
              <label>Reason (optional):</label>
              <input type="text" value={markReason} onChange={(e) => setMarkReason(e.target.value)} />
            </div>

            <div className="modal-actions">
              <button className="checkin-btn" onClick={handleMarkLeave} disabled={isSubmittingMark}>
                {isSubmittingMark ? "Saving..." : "Mark Leave"}
              </button>
              <button className="cancel-btn" onClick={() => setShowMarkModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------------------- Advances tab ---------------------------- */

function AdvancesTab({ resortOptions, showToast }) {
  const [resortFilter, setResortFilter] = useState(ALL_OPTION);
  const [staffFilter, setStaffFilter] = useState(ALL_OPTION);
  const [month, setMonth] = useState(todayDate.getMonth() + 1);
  const [year, setYear] = useState(todayDate.getFullYear());
  const [advances, setAdvances] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);

  const { allOptions: staffOptions, activeOptions: activeStaffOptions } = useStaffOptions(resortFilter.value);

  useEffect(() => {
    setStaffFilter(ALL_OPTION);
  }, [resortFilter]);

  const fetchAdvances = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (resortFilter.value) params.set("resortId", resortFilter.value);
      if (staffFilter.value) params.set("staffId", staffFilter.value);
      const data = await fetchJson(`${config.BASE_URL}/api/staff-advances?${params.toString()}`);
      setAdvances(data.advances || []);
      setSummaries(data.summaries || []);
    } catch (err) {
      console.error(err);
      setAdvances([]);
      setSummaries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resortFilter, staffFilter, month, year]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [advanceStaff, setAdvanceStaff] = useState(null);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [advanceDate, setAdvanceDate] = useState(todayDate);
  const [advanceReason, setAdvanceReason] = useState("");
  const [isSubmittingAdvance, setIsSubmittingAdvance] = useState(false);

  const openAddModal = () => {
    setAdvanceStaff(null);
    setAdvanceAmount("");
    setAdvanceDate(todayDate);
    setAdvanceReason("");
    setShowAddModal(true);
  };

  const handleAddAdvance = async () => {
    if (isSubmittingAdvance) return;
    if (!advanceStaff) {
      showToast("Select a staff member.", "warning");
      return;
    }
    const amount = parseFloat(advanceAmount);
    if (!amount || amount <= 0) {
      showToast("Enter a valid advance amount.", "warning");
      return;
    }

    setIsSubmittingAdvance(true);
    try {
      const res = await fetch(`${config.BASE_URL}/api/staff-advances`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          staffId: advanceStaff.value,
          amount,
          date: toLocalDateStr(advanceDate),
          reason: advanceReason.trim() || null,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || "Failed to record advance");
      }

      showToast("Advance recorded.", "success");
      setShowAddModal(false);
      fetchAdvances();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to record advance. Please try again.", "danger");
    } finally {
      setIsSubmittingAdvance(false);
    }
  };

  return (
    <>
      <div className="staff-management-filters">
        <label>Resort</label>
        <Select
          options={[ALL_OPTION, ...resortOptions]}
          value={resortFilter}
          onChange={setResortFilter}
          isSearchable={false}
          classNamePrefix="react-select"
          className="react-select-container filter-select"
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          styles={themedSelectStyles()}
        />
        <label>Staff</label>
        <Select
          options={[ALL_OPTION, ...staffOptions]}
          value={staffFilter}
          onChange={setStaffFilter}
          isSearchable
          classNamePrefix="react-select"
          className="react-select-container filter-select"
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          styles={themedSelectStyles()}
        />
        <div className="month-picker">
          <Select
            options={MONTH_OPTIONS}
            value={MONTH_OPTIONS.find((o) => o.value === month)}
            onChange={(opt) => setMonth(opt.value)}
            isSearchable={false}
            classNamePrefix="react-select"
            className="react-select-container filter-select"
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            styles={themedSelectStyles()}
          />
          <Select
            options={YEAR_OPTIONS}
            value={YEAR_OPTIONS.find((o) => o.value === year)}
            onChange={(opt) => setYear(opt.value)}
            isSearchable={false}
            classNamePrefix="react-select"
            className="react-select-container filter-select"
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            styles={themedSelectStyles()}
          />
        </div>
        <button className="vt-btn vt-btn-primary" onClick={openAddModal} style={{ marginLeft: "auto" }}>
          + Add Advance
        </button>
      </div>

      {summaries.length > 0 && (
        <div className="staff-advance-summary-row">
          {summaries.map((s) => (
            <div key={s.staffId} className="staff-advance-summary-pill">
              <strong>{s.staffName}</strong>: ₹{Number(s.totalAdvancedThisMonth).toLocaleString()}
              {s.monthlySalary != null && ` / ₹${Number(s.monthlySalary).toLocaleString()} salary`}
            </div>
          ))}
        </div>
      )}

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="attendance-roster-card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Staff</th>
                <th>Resort</th>
                <th>Amount</th>
                <th>Reason</th>
                <th>Given By</th>
              </tr>
            </thead>
            <tbody>
              {advances.length > 0 ? (
                advances.map((a) => (
                  <tr key={a.id}>
                    <td>{formatDateDMY(a.date)}</td>
                    <td>{a.staffName}</td>
                    <td>{a.resortName}</td>
                    <td>₹{Number(a.amount).toLocaleString()}</td>
                    <td>{a.reason || "-"}</td>
                    <td>{a.givenByName || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>No advances for this month</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Add Advance</h3>

            <div className="modal-field">
              <label>Staff:</label>
              <Select
                options={activeStaffOptions}
                value={advanceStaff}
                onChange={setAdvanceStaff}
                placeholder="Select staff..."
                classNamePrefix="react-select"
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>

            <div className="modal-field">
              <label>Amount (₹):</label>
              <input type="number" value={advanceAmount} onChange={(e) => setAdvanceAmount(e.target.value)} />
            </div>

            <div className="modal-field">
              <label>Date:</label>
              <DatePicker
                selected={advanceDate}
                onChange={(date) => setAdvanceDate(date)}
                dateFormat="dd/MM/yyyy"
                className="custom-datepicker"
              />
            </div>

            <div className="modal-field">
              <label>Reason (optional):</label>
              <input type="text" value={advanceReason} onChange={(e) => setAdvanceReason(e.target.value)} />
            </div>

            <div className="modal-actions">
              <button className="checkin-btn" onClick={handleAddAdvance} disabled={isSubmittingAdvance}>
                {isSubmittingAdvance ? "Saving..." : "Add Advance"}
              </button>
              <button className="cancel-btn" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------- Page ------------------------------- */

const StaffManagement = () => {
  const { toasts, showToast, dismissToast } = useToast();
  const [activeTab, setActiveTab] = useState("staff");
  const [resortOptions, setResortOptions] = useState([]);

  useEffect(() => {
    const fetchResorts = async () => {
      try {
        const data = await fetchJson(`${config.BASE_URL}/api/resorts/resort-names-drop-down`);
        setResortOptions(data.map((r) => ({ value: r.id, label: r.name })));
      } catch (err) {
        console.error("Failed to load resorts:", err);
        setResortOptions([]);
      }
    };
    fetchResorts();
  }, []);

  return (
    <div className="staff-management">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="vt-page-header">
        <h2>Staff Management</h2>
      </div>

      <div className="vt-tabs">
        <button
          type="button"
          className={`vt-tab ${activeTab === "staff" ? "active" : ""}`}
          onClick={() => setActiveTab("staff")}
        >
          Staff
        </button>
        <button
          type="button"
          className={`vt-tab ${activeTab === "leaves" ? "active" : ""}`}
          onClick={() => setActiveTab("leaves")}
        >
          Leaves
        </button>
        <button
          type="button"
          className={`vt-tab ${activeTab === "advances" ? "active" : ""}`}
          onClick={() => setActiveTab("advances")}
        >
          Advances
        </button>
      </div>

      {activeTab === "staff" && <StaffTab resortOptions={resortOptions} showToast={showToast} />}
      {activeTab === "leaves" && <LeavesTab resortOptions={resortOptions} showToast={showToast} />}
      {activeTab === "advances" && <AdvancesTab resortOptions={resortOptions} showToast={showToast} />}
    </div>
  );
};

export default StaffManagement;
